<template>
  <div class="page-container model-manage">
    <PageHeader title="模块管理" show-breadcrumb>
      <template #actions>
        <el-button v-if="canAdd" type="success" icon="el-icon-plus" size="small" @click="handleAdd(null)">新增一级模块</el-button>
        <el-button type="primary" icon="el-icon-upload2" size="small" plain @click="openImportDialog">批量导入</el-button>
      </template>
    </PageHeader>

    <div class="model-manage__body">
      <!-- 左侧模块树 -->
      <div class="model-tree">
        <div class="model-tree__header">
          <span class="model-tree__title">模块结构</span>
          <el-tooltip content="刷新" placement="top">
            <el-button type="text" icon="el-icon-refresh" @click="fetchTree" />
          </el-tooltip>
        </div>
        <el-input v-model="treeFilter" placeholder="搜索模块" clearable size="mini" prefix-icon="el-icon-search" class="model-tree__filter" />
        <div v-loading="treeLoading" class="model-tree__wrapper">
          <el-tree
            ref="treeRef"
            :data="treeData"
            :props="treeProps"
            node-key="zmId"
            :expand-on-click-node="false"
            :default-expand-all="true"
            :filter-node-method="filterNode"
            :highlight-current="true"
            @node-click="handleNodeClick"
          >
            <span slot-scope="{ node, data }" class="model-tree-node">
              <!-- 内容形式：用彩色内容标识徽章（同表格图标列） -->
              <span v-if="data.zmLayoutSelf === 3" class="model-tree-node__badge" :class="'model-content-tag--' + (data.zmLayoutThreeForm || 1)">
                <i :class="contentIconClass(data)" />
              </span>
              <!-- 列表/网格形式：展示图标（取首个 URL，zmIcon 支持多图） -->
              <img v-else-if="firstIconUrl(data.zmIcon)" :src="firstIconUrl(data.zmIcon)" class="model-tree-node__img" />
              <!-- 列表/网格无自定义图标：用类型徽章 -->
              <span v-else class="model-tree-node__badge model-content-tag--layout">
                <i :class="layoutIconClass(data)" />
              </span>
              <span class="model-tree-node__label" :title="node.label">{{ node.label }}</span>
              <el-tag size="mini" :type="layoutTagType(data.zmLayoutSelf)" effect="plain">{{ layoutText(data) }}</el-tag>
              <!-- 删除按钮（常驻，danger tag 样式） -->
              <el-tag size="mini" type="danger" effect="plain" class="model-tree-node__delete" @click.stop="handleDelete(data)">删除</el-tag>
            </span>
          </el-tree>
        </div>
      </div>

      <!-- 右侧子模块表格 -->
      <div class="model-table">
        <!-- 当前父级信息卡片（选中某个节点时展示其详细信息） -->
        <div v-if="currentNode" class="model-parent-card">
          <!-- 图标：内容形式(zmLayoutSelf=3)的 zmIcon 是多类型组合 JSON，不作为图标显示 -->
          <img v-if="currentNode.zmLayoutSelf !== 3 && firstIconUrl(currentNode.zmIcon)" :src="firstIconUrl(currentNode.zmIcon)" class="model-parent-card__icon" />
          <div v-else class="model-parent-card__icon model-parent-card__icon--empty">
            <i class="el-icon-folder-opened"></i>
          </div>
          <div class="model-parent-card__info">
            <div class="model-parent-card__title">{{ currentNode.zmTitle }}</div>
            <div class="model-parent-card__meta">
              <el-tag size="mini" type="info">L{{ currentNode.zmLevel }} 级</el-tag>
              <el-tag size="mini" :type="layoutTagType(currentNode.zmLayoutSelf)" effect="plain">{{ layoutText(currentNode) }}</el-tag>
              <span v-if="currentNode.zmTitleSub" class="model-parent-card__sub">{{ currentNode.zmTitleSub }}</span>
              <!-- 内容形式：显示文件类型 + 预览按钮 -->
              <template v-if="currentNode.zmLayoutSelf === 3">
                <el-tag v-if="currentNode.zmLayoutThreeForm === 1" size="mini" type="warning" effect="plain">{{ threeFileText(currentNode.zmLayoutThreeFile) }}</el-tag>
                <el-tag v-else size="mini" type="success" effect="plain">证书</el-tag>
              </template>
            </div>
          </div>
          <div class="model-parent-card__actions">
            <!-- 查看当前模块详情 -->
            <el-button type="info" icon="el-icon-document" size="small" plain @click="handleViewDetail(currentNode)">详情</el-button>
            <!-- 内容形式：预览父级上传的内容（图片/视频/文件/链接） -->
            <el-button v-if="currentNode.zmLayoutSelf === 3 && currentNode.zmLayoutThreeForm === 1" type="warning" icon="el-icon-view" size="small" plain @click="handlePreviewParent">预览内容</el-button>
            <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd(currentNode)">新增子模块</el-button>
          </div>
        </div>

        <!-- 表格标题栏（仅一级列表时展示；选中父级时信息已在上方卡片） -->
        <div v-if="!currentNode" class="model-table__header">
          <span class="model-table__title">一级模块列表</span>
        </div>

        <el-table :data="tableList" v-loading="tableLoading" size="small" stripe :height="tableHeight">
          <el-table-column type="index" label="序号" width="80" align="center" />
          <el-table-column label="图标" width="85" align="center">
            <template slot-scope="{ row }">
              <!-- 内容形式：zmIcon 是多类型组合 JSON，不能当图标显示；改为内容类型标识 -->
              <div v-if="row.zmLayoutSelf === 3" class="model-content-tag" :class="'model-content-tag--' + (row.zmLayoutThreeForm || 1)">
                <i :class="contentIconClass(row)" />
                <span>{{ contentIconText(row) }}</span>
              </div>
              <!-- 列表/网格形式：展示上传的图标缩略图（取首个 URL，zmIcon 支持多图） -->
              <el-image v-else-if="firstIconUrl(row.zmIcon)" :src="firstIconUrl(row.zmIcon)" fit="cover" :preview-src-list="parseIconUrls(row.zmIcon)" style="width:40px;height:40px;border-radius:8px" />
              <!-- 列表/网格无自定义图标：用类型徽章替代占位图 -->
              <div v-else class="model-content-tag model-content-tag--layout">
                <i :class="layoutIconClass(row)" />
                <span>{{ layoutIconText(row) }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="zmTitle" label="模块标题" min-width="140" align="center" show-overflow-tooltip />
          <el-table-column prop="zmTitleSub" label="副标题" min-width="120" align="center" show-overflow-tooltip />
          <el-table-column label="展现形式" width="110" align="center">
            <template slot-scope="{ row }">{{ layoutText(row) }}</template>
          </el-table-column>
          <el-table-column prop="zmOrder" label="排序" width="80" align="center" />
          <el-table-column prop="createusername" label="创建人" width="90" align="center" show-overflow-tooltip />
          <el-table-column label="操作" width="160" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" size="mini" style="color:#409eff" @click="handleViewChildren(row)">下级</el-button>
              <el-button type="text" size="mini" style="color:#e6a23c" @click="handleEdit(row)">编辑</el-button>
              <el-button type="text" size="mini" style="color:#f56c6c" @click="handleDelete(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 新增/编辑模块弹框 -->
    <el-dialog :title="dialogType === 'add' ? '新增模块' : '编辑模块'" :visible.sync="dialogVisible" width="720px" v-dialog-drag-resize custom-class="model-dialog">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px" size="small">
        <el-form-item label="父级模块">
          <span>{{ form._parentName || '（一级模块）' }}</span>
        </el-form-item>
        <el-form-item label="模块标题" prop="zmTitle">
          <el-input v-model.trim="form.zmTitle" placeholder="请输入模块标题" maxlength="50" show-word-limit />
        </el-form-item>

        <!-- 一级模块固定为列表展示，仅需标题/副标题/图标 -->
        <template v-if="!form.zmParentId">
          <el-form-item label="副标题" prop="zmTitleSub">
            <el-input v-model.trim="form.zmTitleSub" placeholder="如：规范参训 助力提升" maxlength="50" show-word-limit />
          </el-form-item>
          <el-form-item label="图标" prop="zmIcon">
            <CjUpload type="picture-card" :filearr="form._iconArr" :limit="9" accept="image/*" @fileChange="onIconChange" @removeAfter="onIconRemove" />
            <div class="form-tip">作为小程序菜单/树节点的展示图标，支持多张；列表展示第一张</div>
          </el-form-item>
        </template>

        <!-- 非一级：选择展现形式 -->
        <el-form-item v-if="form.zmParentId" label="展现形式" prop="zmLayoutSelf">
          <el-radio-group v-model="form.zmLayoutSelf">
            <el-radio :label="1">列表</el-radio>
            <el-radio :label="2">网格</el-radio>
            <el-radio :label="3">内容</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 列表形式：支持图标（可选，支持多张） -->
        <el-form-item v-if="form.zmParentId && form.zmLayoutSelf === 1" label="图标">
          <CjUpload type="picture-card" :filearr="form._iconArr" :limit="9" accept="image/*" @fileChange="onIconChange" @removeAfter="onIconRemove" />
          <div class="form-tip">作为小程序列表项的展示图标（可选，支持多张）</div>
        </el-form-item>

        <!-- 网格形式：需副标题、图标（图标支持多张） -->
        <template v-if="form.zmParentId && form.zmLayoutSelf === 2">
          <el-form-item label="副标题" prop="zmTitleSub">
            <el-input v-model.trim="form.zmTitleSub" placeholder="请输入副标题" maxlength="50" show-word-limit />
          </el-form-item>
          <el-form-item label="图标" prop="zmIcon">
            <CjUpload type="picture-card" :filearr="form._iconArr" :limit="9" accept="image/*" @fileChange="onIconChange" @removeAfter="onIconRemove" />
          </el-form-item>
        </template>

        <!-- 内容形式：区分图片/证书 -->
        <template v-if="form.zmParentId && form.zmLayoutSelf === 3">
          <el-form-item label="内容形式" prop="zmLayoutThreeForm">
            <el-radio-group v-model="form.zmLayoutThreeForm">
              <el-radio :label="1">图片/视频/文件</el-radio>
              <el-radio :label="2">证书</el-radio>
            </el-radio-group>
          </el-form-item>

          <!-- 图片/视频/文件：多类型组合（可添加多个类型行，每行一种文件类型 + 多个文件/链接） -->
          <template v-if="form.zmLayoutThreeForm === 1">
            <el-form-item label="文件内容">
              <div class="content-groups">
                <!-- 动态类型行（卡片式）：每行一种类型，可切换、可增删 -->
                <div v-for="(group, gIdx) in form._contentGroups" :key="gIdx" class="content-group">
                  <div class="content-group__head">
                    <span class="content-group__badge" :class="'content-group__badge--' + group.fileType">{{ threeFileText(group.fileType) }}</span>
                    <el-select v-model="group.fileType" placeholder="文件类型" size="small" style="width:130px" @change="onGroupTypeChange(gIdx)">
                      <el-option :value="1" label="图片" />
                      <el-option :value="2" label="视频" />
                      <el-option :value="3" label="文件" />
                      <el-option :value="4" label="链接" />
                      <el-option :value="5" label="文本" />
                    </el-select>
                    <span class="content-group__count">{{ groupCountText(group) }}</span>
                    <el-button v-if="form._contentGroups.length > 1" type="text" size="mini" icon="el-icon-delete" class="content-group__del" @click="removeContentGroup(gIdx)">移除该类型</el-button>
                  </div>
                  <div class="content-group__body">
                    <!-- 文本域类型：多行文本输入 -->
                    <el-input
                      v-if="group.fileType === 5"
                      v-model="group._text"
                      type="textarea"
                      :rows="4"
                      placeholder="请输入文本内容（支持多行，如基地简介、实训说明等）"
                      maxlength="2000"
                      show-word-limit
                      resize="vertical"
                    />
                    <!-- 链接类型：多链接动态输入 -->
                    <div v-else-if="group.fileType === 4" class="link-list">
                      <div v-for="(link, lIdx) in group._links" :key="lIdx" class="link-item">
                        <el-input v-model.trim="group._links[lIdx]" placeholder="请输入链接地址（http/https 开头）" clearable size="small" />
                        <el-button type="danger" icon="el-icon-delete" size="small" plain circle @click="removeGroupLink(gIdx, lIdx)" :disabled="group._links.length <= 1" />
                      </div>
                      <el-button type="text" icon="el-icon-plus" size="small" @click="addGroupLink(gIdx)">添加链接</el-button>
                    </div>
                    <!-- 图片/视频/文件：上传组件（每行独立一个 CjUpload） -->
                    <CjUpload
                      v-else
                      :type="group.fileType === 1 ? 'picture-card' : 'text'"
                      :filearr="group._arr"
                      :limit="9"
                      :accept="groupAccept(group.fileType)"
                      @fileChange="onGroupFileChange(gIdx, $event)"
                      @removeAfter="onGroupFileRemove(gIdx, $event)"
                    />
                  </div>
                </div>
                <!-- 添加类型行 -->
                <div class="content-groups__add">
                  <el-button type="primary" icon="el-icon-plus" size="small" plain @click="addContentGroup">添加类型</el-button>
                  <span class="form-tip">可组合图片/视频/文件/链接，每种类型支持多个，小程序将按类型分组展示</span>
                </div>
              </div>
            </el-form-item>
          </template>

          <!-- 证书：选择证书归属用户 -->
          <template v-if="form.zmLayoutThreeForm === 2">
            <el-form-item label="证书用户" prop="zauId">
              <el-select v-model="form.zauId" placeholder="请选择证书归属用户" filterable style="width:100%">
                <el-option v-for="u in userOptions" :key="u.zauId" :label="u.zauName + '（' + u.zauPhone + '）'" :value="u.zauId" />
              </el-select>
            </el-form-item>
          </template>
        </template>

        <!-- 模块宣传图：列表/网格形式可上传，作为小程序首页轮播图/基地模块宣传图 -->
        <el-form-item v-if="form.zmLayoutSelf !== 3" label="模块宣传图">
          <CjUpload type="picture-card" :filearr="form._logoArr" :limit="1" accept="image/*" @fileChange="onLogoChange" @removeAfter="onLogoRemove" />
          <div class="form-tip">作为小程序首页轮播图 / 基地模块宣传图，建议上传宽幅横图（如 750×400）</div>
        </el-form-item>

        <el-form-item label="排序">
          <el-input-number v-model="form.zmOrder" :min="0" :max="9999" controls-position="right" />
          <span class="form-tip" style="margin-left:8px">数值越小越靠前</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="submitLoading" @click="submitDialog">确定</el-button>
      </div>
    </el-dialog>

    <!-- 父级内容预览弹窗：按多类型分组预览图片/视频/文件/链接 -->
    <el-dialog title="内容预览" :visible.sync="parentPreviewVisible" width="640px" append-to-body v-dialog-drag-resize>
      <div v-if="currentNode" class="parent-preview">
        <template v-if="previewGroups.length">
          <div v-for="(g, gi) in previewGroups" :key="gi" class="parent-preview__group">
            <div class="parent-preview__group-title">{{ threeFileText(g.fileType) }}（{{ g.fileType === 5 ? (g.text ? g.text.length + ' 字' : '0 字') : g.files.length }}）</div>
            <!-- 文本域 -->
            <div v-if="g.fileType === 5" class="parent-preview__text">{{ g.text }}</div>
            <!-- 图片 -->
            <div v-else-if="g.fileType === 1" class="parent-preview__images">
              <el-image v-for="(f, i) in g.files" :key="i" :src="f.url" fit="contain" :preview-src-list="g.files.map(x => x.url)" :initial-index="i" class="parent-preview__image-item" />
            </div>
            <!-- 视频 -->
            <div v-else-if="g.fileType === 2" class="parent-preview__videos">
              <video v-for="(f, i) in g.files" :key="i" :src="f.url" controls class="parent-preview__video-item" />
            </div>
            <!-- 文件（显示文件名） -->
            <div v-else-if="g.fileType === 3" class="parent-preview__files">
              <div v-for="(f, i) in g.files" :key="i" class="parent-preview__file-row">
                <i class="el-icon-document"></i>
                <span class="parent-preview__file-name">{{ f.name }}</span>
                <el-button type="primary" size="mini" @click="openFile(f.url)">下载/打开</el-button>
              </div>
            </div>
            <!-- 链接 -->
            <div v-else-if="g.fileType === 4" class="parent-preview__links">
              <div v-for="(f, i) in g.files" :key="i" class="parent-preview__link-row">
                <i class="el-icon-link"></i>
                <span class="parent-preview__link-url">{{ f.url }}</span>
                <el-button type="primary" size="mini" @click="openLink(f.url)">打开</el-button>
              </div>
            </div>
          </div>
        </template>
        <!-- 无内容 -->
        <div v-else class="parent-preview__empty">
          <i class="el-icon-warning-outline"></i>
          <span>暂无可预览的内容</span>
        </div>
      </div>
    </el-dialog>

    <!-- 模块详情弹框：展示当前模块的所有信息 -->
    <el-dialog title="模块详情" :visible.sync="detailVisible" width="600px" append-to-body v-dialog-drag-resize>
      <div v-if="detailData" class="model-detail">
        <!-- 图标预览区 -->
        <div class="model-detail__header">
          <!-- 列表/网格形式：图标图片（取首个 URL，zmIcon 支持多图） -->
          <el-image v-if="detailData.zmLayoutSelf !== 3 && firstIconUrl(detailData.zmIcon)" :src="firstIconUrl(detailData.zmIcon)" fit="cover" :preview-src-list="parseIconUrls(detailData.zmIcon)" class="model-detail__icon" />
          <!-- 内容形式：类型徽章 -->
          <div v-else-if="detailData.zmLayoutSelf === 3" class="model-content-tag model-detail__badge" :class="'model-content-tag--' + (detailData.zmLayoutThreeForm || 1)">
            <i :class="contentIconClass(detailData)" />
            <span>{{ contentIconText(detailData) }}</span>
          </div>
          <!-- 无图标占位 -->
          <div v-else class="model-detail__icon model-detail__icon--empty"><i class="el-icon-picture" /></div>
          <div class="model-detail__title-wrap">
            <div class="model-detail__title">{{ detailData.zmTitle }}</div>
            <div class="model-detail__tags">
              <el-tag size="mini" type="info">L{{ detailData.zmLevel }} 级</el-tag>
              <el-tag size="mini" :type="layoutTagType(detailData.zmLayoutSelf)" effect="plain">{{ layoutText(detailData) }}</el-tag>
            </div>
          </div>
        </div>

        <!-- 信息列表 -->
        <el-descriptions :column="2" border size="small" class="model-detail__info">
          <el-descriptions-item label="模块ID">{{ detailData.zmId }}</el-descriptions-item>
          <el-descriptions-item label="父级ID">{{ detailData.zmParentId || '（一级模块）' }}</el-descriptions-item>
          <el-descriptions-item label="模块标题" :span="2">{{ detailData.zmTitle }}</el-descriptions-item>
          <el-descriptions-item label="副标题" :span="2">{{ detailData.zmTitleSub || '—' }}</el-descriptions-item>
          <el-descriptions-item label="模块级别">L{{ detailData.zmLevel }}</el-descriptions-item>
          <el-descriptions-item label="排序">{{ detailData.zmOrder }}</el-descriptions-item>
          <el-descriptions-item label="展现形式">{{ layoutText(detailData) }}</el-descriptions-item>
          <el-descriptions-item label="内容形式">
            <span v-if="detailData.zmLayoutSelf === 3">{{ Number(detailData.zmLayoutThreeForm) === 2 ? '证书' : '图文' }}</span>
            <span v-else>—</span>
          </el-descriptions-item>
          <!-- 内容形式专属信息 -->
          <template v-if="detailData.zmLayoutSelf === 3">
            <!-- 图文：按多类型分组展示文件内容 -->
            <template v-if="Number(detailData.zmLayoutThreeForm) === 1">
              <el-descriptions-item v-for="(g, gi) in detailFileGroups" :key="gi" :label="threeFileText(g.fileType) + '（' + (g.fileType === 5 ? (g.text ? g.text.length + ' 字' : '0 字') : g.files.length) + '）'" :span="2">
                <!-- 文本域：展示文本内容（保留换行） -->
                <template v-if="g.fileType === 5">
                  <div class="detail-text-block">{{ g.text }}</div>
                </template>
                <!-- 图片：缩略图预览（preview-src-list 需 URL 数组） -->
                <template v-else-if="g.fileType === 1">
                  <el-image v-for="(f, i) in g.files" :key="i" :src="f.url" fit="cover" :preview-src-list="g.files.map(x => x.url)" :initial-index="i" class="detail-image-item" />
                </template>
                <!-- 视频 -->
                <template v-else-if="g.fileType === 2">
                  <video v-for="(f, i) in g.files" :key="i" :src="f.url" controls class="detail-video-item" />
                </template>
                <!-- 文件（显示文件名） -->
                <template v-else-if="g.fileType === 3">
                  <div v-for="(f, i) in g.files" :key="i" class="detail-url-line">
                    <span class="detail-url-idx">{{ i + 1 }}.</span>
                    <el-link type="primary" :href="f.url" target="_blank" :underline="false">{{ f.name }}</el-link>
                  </div>
                </template>
                <!-- 链接（显示链接地址） -->
                <template v-else-if="g.fileType === 4">
                  <div v-for="(f, i) in g.files" :key="i" class="detail-url-line">
                    <span class="detail-url-idx">{{ i + 1 }}.</span>
                    <el-link type="primary" :href="f.url" target="_blank" :underline="false">{{ f.url }}</el-link>
                  </div>
                </template>
              </el-descriptions-item>
              <el-descriptions-item v-if="!detailFileGroups.length" label="文件内容" :span="2">—</el-descriptions-item>
            </template>
            <el-descriptions-item v-if="Number(detailData.zmLayoutThreeForm) === 2" label="证书用户" :span="2">{{ detailData.zauName || detailData.zauId || '—' }}</el-descriptions-item>
          </template>
          <!-- 图标地址（列表/网格形式，支持多图） -->
          <el-descriptions-item v-if="detailData.zmLayoutSelf !== 3 && parseIconUrls(detailData.zmIcon).length" label="图标地址" :span="2">
            <div v-for="(u, i) in parseIconUrls(detailData.zmIcon)" :key="i" class="detail-url-line">
              <span class="detail-url-idx">{{ i + 1 }}.</span>
              <el-link type="primary" :href="u" target="_blank" :underline="false">{{ u }}</el-link>
            </div>
          </el-descriptions-item>
          <!-- 模块宣传图（列表/网格形式） -->
          <el-descriptions-item v-if="detailData.zmLayoutSelf !== 3 && detailData.zmLogo" label="模块宣传图" :span="2">
            <el-image :src="detailData.zmLogo" fit="contain" :preview-src-list="[detailData.zmLogo]" style="max-width:240px;border-radius:8px" />
          </el-descriptions-item>
          <!-- 创建信息 -->
          <el-descriptions-item label="创建人">{{ detailData.createusername || '—' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ detailData.createtime || '—' }}</el-descriptions-item>
        </el-descriptions>
      </div>
      <div slot="footer">
        <el-button type="primary" size="small" @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 批量导入弹框：父级选择 + 文件上传 + 导出模板 -->
    <el-dialog title="批量导入" :visible.sync="importDialogVisible" width="560px" append-to-body v-dialog-drag-resize @open="onImportDialogOpen">
      <el-form label-width="100px" size="small">
        <!-- 父级模块选择（树形级联，含"一级模块"选项） -->
        <el-form-item label="导入位置">
          <el-cascader
            v-model="importParentPath"
            :options="parentOptions"
            :props="cascaderProps"
            placeholder="请选择导入的父级模块"
            filterable
            clearable
            style="width:100%"
            @change="onParentChange"
          >
            <template slot-scope="{ data }">
              <span>{{ data.label }}</span>
            </template>
          </el-cascader>
          <div class="form-tip">不选择则导入为一级模块</div>
        </el-form-item>

        <!-- 文件上传 -->
        <el-form-item label="Excel文件">
          <el-upload
            ref="importUpload"
            action="#"
            :auto-upload="false"
            :limit="1"
            accept=".xlsx,.xls"
            :on-change="onImportFileChange"
            :on-remove="onImportFileRemove"
            :file-list="importFileList"
          >
            <el-button size="small" type="primary" plain icon="el-icon-folder-opened">选择文件</el-button>
            <div slot="tip" class="el-upload__tip">仅支持 .xlsx / .xls 格式</div>
          </el-upload>
        </el-form-item>

        <!-- 导出模板按钮（弹框内） -->
        <el-form-item label="">
          <el-button type="text" icon="el-icon-download" :loading="exportLoading" @click="handleExportTemplate">下载导入模板</el-button>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="importLoading" :disabled="!importFile" @click="submitImport">开始导入</el-button>
      </div>
    </el-dialog>

    <!-- 导入结果弹窗：显示成功/失败明细 -->
    <el-dialog title="导入结果" :visible.sync="importResultVisible" width="640px" append-to-body v-dialog-drag-resize>
      <div v-if="importResult" v-loading="importLoading" class="import-result">
        <!-- 统计概览 -->
        <div class="import-result__summary">
          <div class="import-result__stat">
            <span class="import-result__num">{{ importResult.total }}</span>
            <span class="import-result__label">总数</span>
          </div>
          <div class="import-result__stat import-result__stat--success">
            <span class="import-result__num">{{ importResult.successCount }}</span>
            <span class="import-result__label">成功</span>
          </div>
          <div class="import-result__stat import-result__stat--fail">
            <span class="import-result__num">{{ importResult.failCount }}</span>
            <span class="import-result__label">失败</span>
          </div>
        </div>
        <!-- 失败明细表格 -->
        <el-table v-if="importResult.list && importResult.list.length" :data="importResult.list" size="mini" stripe max-height="320" class="import-result__table">
          <el-table-column prop="rowIndex" label="行号" width="70" align="center" />
          <el-table-column prop="zmTitle" label="模块标题" min-width="140" align="center" show-overflow-tooltip />
          <el-table-column label="状态" width="80" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.status === '成功' ? 'success' : 'danger'">{{ row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="statusMsg" label="说明" min-width="160" align="center" show-overflow-tooltip />
        </el-table>
      </div>
      <div slot="footer">
        <el-button type="primary" size="small" @click="importResultVisible = false">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '@/components/PageHeader.vue'
import CjUpload from '@/components/cj-upload.vue'
import { getZqsxModelList, addZqsxModel, updateZqsxModel, deleteZqsxModel, exportZqsxModelTemplate, importZqsxModelCer, getZqsxAuthUserList } from '@/api/api'

// 展现形式字典：1列表 2网格 3内容
const LAYOUT_TEXT = { 1: '列表', 2: '网格', 3: '内容' }
const LAYOUT_TAG = { 1: 'info', 2: 'success', 3: 'warning' }

export default {
  name: 'ModelManage',
  components: { PageHeader, CjUpload },
  data () {
    return {
      treeLoading: false,
      tableLoading: false,
      // 导入/导出相关状态
      exportLoading: false,
      importLoading: false,
      importResultVisible: false,
      importResult: null,
      // 模块详情弹框状态
      detailVisible: false,
      detailData: null,
      // 批量导入弹框状态
      importDialogVisible: false,
      importParentId: 0,          // 导入的父级模块 id（0=一级模块）
      importFile: null,            // 选中的 Excel 文件
      importFileList: [],          // el-upload 文件列表（用于清空）
      importParentPath: [],        // 级联选择器路径（节点 id 数组）
      parentOptions: [],           // 父级模块级联选项（含"一级模块"根节点）
      cascaderProps: { value: 'value', label: 'label', children: 'children', checkStrictly: true, emitPath: false },
      treeData: [],
      treeProps: { label: 'zmTitle', children: 'children' },
      treeFilter: '',
      // 当前选中的树节点（null 表示根级）
      currentNode: null,
      // 右侧表格：当前节点下的子模块列表
      tableList: [],
      tableHeight: 400,
      userOptions: [],
      dialogVisible: false,
      dialogType: 'add',
      // 父级内容预览弹窗
      parentPreviewVisible: false,
      submitLoading: false,
      form: this.buildForm(),
      rules: {
        zmTitle: [{ required: true, message: '请输入模块标题', trigger: 'blur' }],
        zmLayoutSelf: [{ required: true, message: '请选择展现形式', trigger: 'change' }],
        zmLayoutThreeForm: [{ required: true, message: '请选择内容形式', trigger: 'change' }],
        zmLayoutThreeFile: [{ required: true, message: '请选择文件类型', trigger: 'change' }],
        zauId: [{ required: true, message: '请选择证书用户', trigger: 'change' }]
      }
    }
  },
  computed: {
    userInfo () { return this.$store.state.user.userInfo || {} },
    // 系统管理员(1)和项目管理员(2)均可管理模块
    canAdd () { return this.userInfo.zauPost === 1 || this.userInfo.zauPost === 2 },
    // 父级内容预览：解析 currentNode.zmIcon 为多类型分组数组
    previewGroups () {
      return this.parseFileRecord(this.currentNode && this.currentNode.zmIcon)
    },
    // 详情弹框：解析 detailData.zmIcon 为多类型分组数组
    detailFileGroups () {
      return this.parseFileRecord(this.detailData && this.detailData.zmIcon)
    }
  },
  watch: {
    treeFilter (val) {
      this.$refs.treeRef && this.$refs.treeRef.filter(val)
    }
  },
  created () { this.fetchTree() },
  mounted () {
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight)
  },
  beforeDestroy () { window.removeEventListener('resize', this.calcTableHeight) },
  methods: {
    // 解析内容文件字段 zmIcon 为多类型分组数组
    // 返回 [{fileType, files:[{url,name}]} 或 {fileType:5, text:'...'}]
    // 兼容多种历史格式：
    //   1. v3 新格式：[{fileType:5, text:'...'}]（文本域）/ [{fileType, files:[{url,name}]}]（文件类）
    //   2. v1 旧格式：[{fileType, urls:[...]}] → 自动补 name
    //   3. 更旧：[url1,url2] 纯字符串数组 → 归为 fileType:3
    //   4. 最旧：单 URL 字符串 → 归为 fileType:3
    parseFileRecord (raw) {
      const urlToName = (u) => {
        if (!u) return '文件'
        const clean = String(u).split('?')[0]
        const segs = clean.split('/')
        return decodeURIComponent(segs[segs.length - 1] || '文件')
      }
      const normalize = (groups) => groups
        .filter(g => g && ((g.files && g.files.length) || (g.urls && g.urls.length) || (Number(g.fileType) === 5 && g.text)))
        .map(g => {
          // 文本域类型：保留 text 字段，无 files
          if (Number(g.fileType) === 5) return { fileType: 5, text: g.text || '', files: [] }
          // 文件类：优先用 files（v2），否则从 urls（v1）降级
          const files = (g.files && g.files.length)
            ? g.files.filter(f => f && f.url).map(f => ({ url: f.url, name: f.name || urlToName(f.url) }))
            : (g.urls || []).filter(u => u).map(u => ({ url: u, name: urlToName(u) }))
          return { fileType: Number(g.fileType), files }
        })
      if (!raw) return []
      if (Array.isArray(raw)) {
        if (raw.length && typeof raw[0] === 'object' && raw[0] !== null) {
          return normalize(raw)
        }
        // 纯字符串数组
        return [{ fileType: 3, files: raw.filter(u => u && typeof u === 'string').map(u => ({ url: u, name: urlToName(u) })) }]
      }
      const str = String(raw).trim()
      if (str.startsWith('[')) {
        try {
          const arr = JSON.parse(str)
          if (Array.isArray(arr)) {
            if (arr.length && typeof arr[0] === 'object' && arr[0] !== null) return normalize(arr)
            return [{ fileType: 3, files: arr.filter(u => u && typeof u === 'string').map(u => ({ url: u, name: urlToName(u) })) }]
          }
        } catch (e) { /* 兜底 */ }
      }
      return [{ fileType: 3, files: [{ url: str, name: urlToName(str) }] }]
    },
    // ============ 多类型组合（内容形式=图文）方法 ============
    // 添加一个类型行
    addContentGroup () {
      this.form._contentGroups.push({ fileType: 1, _files: [], _arr: [], _links: [''], _text: '' })
    },
    // 删除某个类型行（至少保留1行）
    removeContentGroup (idx) {
      if (this.form._contentGroups.length <= 1) return
      this.form._contentGroups.splice(idx, 1)
    },
    // 切换某行类型时，清空该行的内容（避免类型数据混乱）
    onGroupTypeChange (idx) {
      const g = this.form._contentGroups[idx]
      g._files = []
      g._arr = []
      g._links = ['']
      g._text = ''
    },
    // 卡片头部的数量统计文字（按类型分别计算）
    groupCountText (g) {
      if (g.fileType === 5) return (g._text && g._text.trim()) ? (g._text.length + ' 字') : '0 字'
      if (g.fileType === 4) return g._links.filter(l => l && l.trim()).length + ' 个链接'
      return (g._files || []).length + ' 个文件'
    },
    // 某行添加一个链接输入
    addGroupLink (idx) {
      this.form._contentGroups[idx]._links.push('')
    },
    // 某行删除一个链接输入（至少保留1行）
    removeGroupLink (gIdx, lIdx) {
      const links = this.form._contentGroups[gIdx]._links
      if (links.length <= 1) return
      links.splice(lIdx, 1)
    },
    // 某行 CjUpload 上传回调：files 为该组件当前全部文件对象数组 [{url, name}]
    onGroupFileChange (gIdx, files) {
      if (Array.isArray(files)) {
        const g = this.form._contentGroups[gIdx]
        g._files = files.filter(f => f && f.url).map(f => ({ url: f.url, name: f.name || '文件' }))
        g._arr = g._files.map(f => ({ url: f.url, name: f.name }))
      }
    },
    // 某行 CjUpload 删除回调：url 为被删除的URL
    onGroupFileRemove (gIdx, url) {
      const g = this.form._contentGroups[gIdx]
      if (url) {
        g._files = g._files.filter(f => f.url !== url)
        g._arr = g._files.map(f => ({ url: f.url, name: f.name }))
      } else {
        g._files = []
        g._arr = []
      }
    },
    // 某行 accept 计算（图片/视频/文件类型用）
    groupAccept (fileType) {
      const map = { 1: 'image/*', 2: 'video/*', 3: '', 4: '' }
      return map[fileType] || ''
    },
    layoutText (row) {
      // 一级模块固定为列表
      if (!row.zmParentId) return '一级'
      return LAYOUT_TEXT[row.zmLayoutSelf] || '列表'
    },
    layoutTagType (v) { return LAYOUT_TAG[v] || 'info' },
    // 级别排序：按 zmLevel 数值升序/降序
    sortByLevel (a, b) {
      return Number(a.zmLevel || 0) - Number(b.zmLevel || 0)
    },
    // 内容形式（zmLayoutSelf=3）的图标列：根据内容子类型显示标识
    // zmLayoutThreeForm: 1图文 2证书；zmLayoutThreeFile: 1图片 2视频 3文件 4链接
    contentIconClass (row) {
      // 证书优先
      if (Number(row.zmLayoutThreeForm) === 2) return 'el-icon-medal'
      const ft = Number(row.zmLayoutThreeFile || 1)
      return { 1: 'el-icon-picture', 2: 'el-icon-video-camera', 3: 'el-icon-document', 4: 'el-icon-link' }[ft] || 'el-icon-document'
    },
    contentIconText (row) {
      if (Number(row.zmLayoutThreeForm) === 2) return '证书'
      const ft = Number(row.zmLayoutThreeFile || 1)
      return { 1: '图片', 2: '视频', 3: '文件', 4: '链接' }[ft] || '文件'
    },
    // 展现形式（列表/网格）的图标列标识，仅当无自定义图标图片时使用
    // zmLayoutSelf: 1列表 2网格
    layoutIconClass (row) {
      const l = Number(row.zmLayoutSelf)
      if (l === 2) return 'el-icon-s-grid'
      if (l === 1) return 'el-icon-menu'
      return 'el-icon-folder'
    },
    layoutIconText (row) {
      const l = Number(row.zmLayoutSelf)
      if (l === 2) return '网格'
      if (l === 1) return '列表'
      return '目录'
    },
    buildForm () {
      return {
        zmId: null,
        zmParentId: null,
        zmParentName: '',
        zmLevel: null,
        zmTitle: '',
        zmTitleSub: '',
        zmIcon: '',
        zmLogo: '',           // 模块宣传图（列表/网格形式，作为小程序首页轮播图/基地宣传图）
        zmOrder: 0,
        zmLayoutSelf: 1,
        zmLayoutThreeForm: 1,
        zmLayoutThreeFile: 1,
        zauId: null,
        _parentName: '',
        // 非内容形式（一级/列表/网格）图标 URL 数组，支持多张
        // 提交时：1 个 → 纯字符串；多个 → JSON.stringify([...]) 字符串数组
        _iconUrls: [],
        // CjUpload 回显用文件对象数组 [{url, name}]
        _iconArr: [],
        // 模块宣传图回显（单值，列表/网格形式）
        _logoArr: [],
        // 内容形式=图文：多类型组合，每行一个 {fileType, _files, _arr, _links, _text}
        // fileType: 1图片 2视频 3文件 4链接 5文本域；_files/_arr 上传文件；_links 链接；_text 文本域内容
        _contentGroups: [
          { fileType: 1, _files: [], _arr: [], _links: [''], _text: '' }
        ]
      }
    },
    // ============ 非内容形式 zmIcon 解析工具 ============
    // 将后端 zmIcon 字段值解析为 URL 数组
    // 兼容三种历史/当前格式：
    //   1. 纯 URL 字符串：'https://...png' → ['https://...png']
    //   2. JSON 字符串数组：'["url1","url2"]' → ['url1','url2']
    //   3. 空/null：[] 
    //   注：内容形式的 zmIcon 是多类型组合 JSON（含 fileType），不在此处理，请用 parseFileRecord
    parseIconUrls (raw) {
      if (!raw) return []
      if (Array.isArray(raw)) {
        // 数组：可能是 URL 字符串数组，也可能是内容形式的对象数组（含 fileType）→ 后者忽略
        const urls = raw.filter(x => typeof x === 'string' && x)
        if (urls.length) return urls
        return []
      }
      const str = String(raw).trim()
      if (!str) return []
      // 尝试 JSON 解析
      if (str.startsWith('[')) {
        try {
          const arr = JSON.parse(str)
          if (Array.isArray(arr)) {
            const urls = arr.filter(x => typeof x === 'string' && x)
            if (urls.length) return urls
            // 若解析出对象数组（内容形式数据），不当作图标处理
          }
        } catch (e) { /* 解析失败，按纯字符串处理 */ }
      }
      // 兜底：当作单个 URL 字符串
      return [str]
    },
    // 取首个图标 URL（用于表格/树/卡片等单图标展示场景）
    firstIconUrl (raw) {
      const urls = this.parseIconUrls(raw)
      return urls.length ? urls[0] : ''
    },
    calcTableHeight () {
      const el = this.$el.querySelector('.model-table .el-table')
      if (!el) return
      this.tableHeight = window.innerHeight - el.getBoundingClientRect().top - 60
    },
    filterNode (value, data) {
      if (!value) return true
      return (data.zmTitle || '').includes(value)
    },
    // 拉取整棵模块树：逐级展开构建。这里采用按父级分批查询组装树
    async fetchTree () {
      this.treeLoading = true
      try {
        // 先拉一级
        const res = await getZqsxModelList({ zmParentId: 0, currentPage: 1, pageSize: 999 })
        const roots = res.data || []
        // 递归拉取每个节点的子级
        await this.fillChildren(roots)
        this.treeData = roots
        // 默认展示一级模块表格
        this.tableList = roots
        this.currentNode = null
      } catch (e) {
        this.treeData = []
        this.tableList = []
      } finally {
        this.treeLoading = false
      }
    },
    // 递归填充子节点
    async fillChildren (nodes) {
      if (!nodes || !nodes.length) return
      await Promise.all(nodes.map(async (node) => {
        try {
          const res = await getZqsxModelList({ zmParentId: node.zmId, currentPage: 1, pageSize: 999 })
          const children = res.data || []
          node.children = children.length ? children : null
          await this.fillChildren(children)
        } catch (e) {
          node.children = null
        }
      }))
    },
    // 点击树节点：右侧展示该节点的子模块
    handleNodeClick (data) {
      this.currentNode = data
      this.fetchChildren(data)
    },
    async fetchChildren (node) {
      this.tableLoading = true
      try {
        const res = await getZqsxModelList({ zmParentId: node.zmId, currentPage: 1, pageSize: 999 })
        this.tableList = res.data || []
      } catch (e) {
        this.tableList = []
      } finally {
        this.tableLoading = false
      }
    },
    // 查看模块详情：展示当前模块的所有信息
    async handleViewDetail (row) {
      this.detailData = { ...row }
      // 证书类型：若 userOptions 未加载或找不到对应用户，则加载一次用户列表以匹配证书用户名
      if (Number(row.zmLayoutThreeForm) === 2 && row.zauId) {
        if (!this.userOptions.length) {
          try {
            const res = await getZqsxAuthUserList({ currentPage: 1, pageSize: 9999 })
            this.userOptions = res.data || []
          } catch (e) { this.userOptions = [] }
        }
        const u = this.userOptions.find(u => u.zauId === row.zauId)
        if (u) this.detailData.zauName = u.zauName + '（' + u.zauPhone + '）'
      }
      this.detailVisible = true
    },
    handleViewChildren (row) {
      // 点击表格"查看下级"：定位到树节点并展示其子级
      this.$refs.treeRef && this.$refs.treeRef.setCurrentKey(row.zmId)
      this.currentNode = row
      this.fetchChildren(row)
    },
    // 刷新当前选中父级的表格数据（保留 currentNode 不变，新增/编辑后调用）
    // 同时刷新左侧树，保证树节点与最新数据一致
    async refreshCurrent () {
      // 先记录当前选中节点的 zmId（fetchTree 会重置 currentNode=null）
      const currentId = this.currentNode ? this.currentNode.zmId : null
      await this.fetchTree()
      // fetchTree 会重置 currentNode=null 并展示一级列表，这里需用 zmId 恢复当前选中
      if (currentId) {
        const updated = this.findNodeInTree(this.treeData, currentId)
        if (updated) {
          this.currentNode = updated
          this.$refs.treeRef && this.$refs.treeRef.setCurrentKey(updated.zmId)
          await this.fetchChildren(updated)
        } else {
          this.tableList = []
        }
      }
    },
    // 在树中递归查找指定 zmId 的节点
    findNodeInTree (nodes, zmId) {
      if (!nodes || !nodes.length) return null
      for (const n of nodes) {
        if (n.zmId === zmId) return n
        if (n.children) {
          const found = this.findNodeInTree(n.children, zmId)
          if (found) return found
        }
      }
      return null
    },
    // 打开父级内容预览弹窗
    handlePreviewParent () {
      this.parentPreviewVisible = true
    },
    // 内容文件类型文字（zmLayoutThreeFile: 1图片 2视频 3文件 4链接）
    threeFileText (v) {
      const map = { 1: '图片', 2: '视频', 3: '文件', 4: '链接', 5: '文本' }
      return map[v] || '内容'
    },
    // 从 URL 提取文件名
    extractFileName (url) {
      if (!url) return '文件'
      const clean = url.split('?')[0]
      const segs = clean.split('/')
      return decodeURIComponent(segs[segs.length - 1] || '文件')
    },
    // 打开文件（新窗口下载）
    openFile (url) {
      window.open(url, '_blank')
    },
    // 打开链接（新窗口）
    openLink (url) {
      window.open(url, '_blank')
    },
    async fetchUsers () {
      // 证书归属用户：拉取当前项目下所有用户（含普通用户）
      try {
        const res = await getZqsxAuthUserList({ currentPage: 1, pageSize: 999 })
        this.userOptions = res.data || []
      } catch (e) { this.userOptions = [] }
    },
    handleAdd (parent) {
      this.dialogType = 'add'
      this.form = this.buildForm()
      if (parent) {
        this.form.zmParentId = parent.zmId
        this.form.zmLevel = (parent.zmLevel || 1) + 1
        this.form._parentName = parent.zmTitle
      } else {
        // 一级模块
        this.form.zmParentId = 0
        this.form.zmLevel = 1
        this.form._parentName = ''
      }
      this.dialogVisible = true
      // 若可能创建证书内容，预拉取用户列表
      this.fetchUsers()
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    handleEdit (row) {
      this.dialogType = 'edit'
      // 内容形式=图文：zmIcon 存多类型组合 JSON 字符串 [{fileType, urls}]
      // 判断图标字段是否作为"内容文件"使用（内容形式+图文 时 zmIcon 存的是文件URL，非图标）
      const isContentImageText = row.zmLayoutSelf === 3 && row.zmLayoutThreeForm === 1
      // 回显多类型组合：解析为 _contentGroups
      let contentGroups = [{ fileType: 1, _files: [], _arr: [], _links: [''], _text: '' }]
      if (isContentImageText) {
        const groups = this.parseFileRecord(row.zmIcon)
        if (groups.length) {
          contentGroups = groups.map(g => {
            if (g.fileType === 5) {
              // 文本域：回显到 _text
              return { fileType: 5, _files: [], _arr: [], _links: [''], _text: g.text || '' }
            }
            if (g.fileType === 4) {
              // 链接类型：回显到 _links（files 中的 url 即链接地址）
              const links = (g.files || []).map(f => f.url).filter(u => u)
              return { fileType: 4, _files: [], _arr: [], _links: links.length ? links : [''], _text: '' }
            }
            // 图片/视频/文件：回显到 _files + _arr（保留 name）
            return {
              fileType: g.fileType,
              _files: (g.files || []).map(f => ({ url: f.url, name: f.name || '文件' })),
              _arr: (g.files || []).map(f => ({ url: f.url, name: f.name || '文件' })),
              _links: [''],
              _text: ''
            }
          })
        }
      }
      this.form = {
        zmId: row.zmId,
        zmParentId: row.zmParentId,
        zmParentName: '',
        zmLevel: row.zmLevel,
        zmTitle: row.zmTitle || '',
        zmTitleSub: row.zmTitleSub || '',
        zmIcon: row.zmIcon || '',
        zmLogo: row.zmLogo || '',
        zmOrder: row.zmOrder != null ? row.zmOrder : 0,
        zmLayoutSelf: row.zmLayoutSelf || 1,
        zmLayoutThreeForm: row.zmLayoutThreeForm || 1,
        zmLayoutThreeFile: row.zmLayoutThreeFile || 1,
        zauId: row.zauId || null,
        _parentName: row.zmParentId ? this.findParentName(row.zmParentId) : '',
        // 图标回显：内容形式=图文时 zmIcon 是多类型组合 JSON，不作为图标回显
        // 非内容形式时：解析为 URL 数组 + CjUpload 文件对象数组（支持多图）
        _iconUrls: !isContentImageText ? this.parseIconUrls(row.zmIcon) : [],
        _iconArr: !isContentImageText
          ? this.parseIconUrls(row.zmIcon).map(u => ({ url: u, name: this.extractFileName(u) }))
          : [],
        // 模块宣传图回显（列表/网格形式）
        _logoArr: row.zmLogo ? [{ url: row.zmLogo, name: '模块宣传图' }] : [],
        // 内容形式=图文：多类型组合回显
        _contentGroups: contentGroups
      }
      this.dialogVisible = true
      if (this.form.zmLayoutSelf === 3 && this.form.zmLayoutThreeForm === 2) this.fetchUsers()
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    // 在树中查找父节点名
    findParentName (parentId) {
      let name = ''
      const walk = (nodes) => {
        for (const n of nodes) {
          if (n.zmId === parentId) { name = n.zmTitle; return true }
          if (n.children && walk(n.children)) return true
        }
        return false
      }
      walk(this.treeData)
      return name
    },
    // 图标上传回调：CjUpload emit 当前全部文件对象数组 [{url, name}]
    // 同步维护 _iconUrls（URL 数组，提交用）与 _iconArr（回显用）
    // 提交时按数量决定：1 个 → 纯字符串；多个 → JSON.stringify([...]) 字符串数组
    onIconChange (files) {
      if (Array.isArray(files)) {
        const urls = files.filter(f => f && f.url).map(f => f.url)
        this.form._iconUrls = urls
        this.form._iconArr = files.filter(f => f && f.url).map(f => ({ url: f.url, name: f.name || this.extractFileName(f.url) }))
      }
    },
    // 图标删除回调：url 为被删除的文件 URL，从 _iconUrls/_iconArr 同步剔除
    onIconRemove (url) {
      if (url) {
        this.form._iconUrls = this.form._iconUrls.filter(u => u !== url)
        this.form._iconArr = this.form._iconArr.filter(f => f.url !== url)
      } else {
        // url 为空时表示全部清空
        this.form._iconUrls = []
        this.form._iconArr = []
      }
    },
    // 模块宣传图上传回调：回填 URL 到 zmLogo（单值，纯 URL 字符串）
    onLogoChange (urls) {
      if (urls && urls.length) {
        const last = urls[urls.length - 1]
        this.form.zmLogo = (last && typeof last === 'object') ? last.url : last
      }
    },
    onLogoRemove () { this.form.zmLogo = '' },
    // 自定义校验：根据展现形式动态校验副标题/图标/文件
    validateByLayout () {
      // 一级模块：标题、副标题、图标必填（后端强制）
      if (!this.form.zmParentId) {
        if (!this.form.zmTitle || !this.form.zmTitleSub || !this.form._iconUrls.length) {
          this.$message.warning('一级模块需填写标题、副标题、图标')
          return false
        }
        return true
      }
      // 列表：仅需标题
      if (this.form.zmLayoutSelf === 1) {
        if (!this.form.zmTitle) { this.$message.warning('请填写模块标题'); return false }
        return true
      }
      // 网格：标题、副标题、图标必填
      if (this.form.zmLayoutSelf === 2) {
        if (!this.form.zmTitle || !this.form.zmTitleSub || !this.form._iconUrls.length) {
          this.$message.warning('网格形式需填写标题、副标题、图标')
          return false
        }
        return true
      }
      // 内容形式
      if (this.form.zmLayoutSelf === 3) {
        if (this.form.zmLayoutThreeForm === 1) {
          // 多类型组合：至少一个类型行有内容
          const hasContent = (this.form._contentGroups || []).some(g => {
            if (g.fileType === 5) return g._text && g._text.trim()
            if (g.fileType === 4) return (g._links || []).some(l => l && l.trim())
            return (g._files || []).length > 0
          })
          if (!hasContent) { this.$message.warning('请至少添加一项文件内容'); return false }
          // 校验所有链接格式
          for (const g of (this.form._contentGroups || [])) {
            if (g.fileType === 4) {
              const bad = (g._links || []).find(l => l && l.trim() && !/^https?:\/\//i.test(l.trim()))
              if (bad) { this.$message.warning('链接需以 http:// 或 https:// 开头'); return false }
            }
          }
        } else if (this.form.zmLayoutThreeForm === 2) {
          // 证书需选择用户
          if (!this.form.zauId) { this.$message.warning('请选择证书用户'); return false }
        }
        return true
      }
      return false
    },
    submitDialog () {
      // 先走 el-form 基础校验，再走按展现形式的补充校验
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        if (!this.validateByLayout()) return
        this.submitLoading = true
        try {
          // 根据展现形式计算 zmIcon 字段值
          // 约定：
          //   - 内容形式(zmLayoutSelf=3)+图文(zmLayoutThreeForm=1)：zmIcon 存多类型组合 JSON 字符串
          //     格式：[{fileType:1, files:[{url,name}]}, {fileType:5, text:'...'}, ...]
          //   - 非内容形式（一级/列表/网格）：zmIcon 由 _iconUrls 决定
          //     · 0 个 URL → 空字符串 ''
          //     · 1 个 URL → 纯 URL 字符串（兼容历史单值数据）
          //     · 多个 URL → JSON.stringify([...]) 字符串数组
          //     注意：不能直接传数组对象，否则 qs.stringify 会序列化成 zmIcon[0]=...&zmIcon[1]=... 报错
          let zmIconValue = ''
          const isContent = this.form.zmParentId && this.form.zmLayoutSelf === 3 && this.form.zmLayoutThreeForm === 1
          if (isContent) {
            // 内容形式=图文：遍历多类型组，收集每行非空内容
            const groups = []
            ;(this.form._contentGroups || []).forEach(g => {
              if (g.fileType === 5) {
                // 文本域：收集非空文本
                const text = (g._text || '').trim()
                if (text) groups.push({ fileType: 5, text })
              } else if (g.fileType === 4) {
                // 链接类型：收集非空链接，name 用链接地址本身
                const links = (g._links || []).map(l => (l || '').trim()).filter(l => l)
                if (links.length) groups.push({ fileType: 4, files: links.map(u => ({ url: u, name: u })) })
              } else {
                // 图片/视频/文件：使用上传的文件对象数组 [{url, name}]
                const files = (g._files || []).filter(f => f && f.url).map(f => ({ url: f.url, name: f.name || '文件' }))
                if (files.length) groups.push({ fileType: g.fileType, files })
              }
            })
            zmIconValue = JSON.stringify(groups)
          } else {
            // 非内容形式：按 _iconUrls 数量决定字符串/JSON 数组
            const urls = (this.form._iconUrls || []).filter(u => u)
            if (urls.length === 1) zmIconValue = urls[0]
            else if (urls.length > 1) zmIconValue = JSON.stringify(urls)
          }
          const payload = {
            zmTitle: this.form.zmTitle,
            zmTitleSub: this.form.zmTitleSub,
            zmIcon: zmIconValue,
            // 模块宣传图：仅列表/网格形式有意义，内容形式传空字符串
            zmLogo: this.form.zmLayoutSelf !== 3 ? (this.form.zmLogo || '') : '',
            zmOrder: this.form.zmOrder,
            zmLayoutSelf: this.form.zmLayoutSelf,
            zmLayoutThreeForm: this.form.zmLayoutThreeForm,
            zmLayoutThreeFile: this.form.zmLayoutThreeFile,
            zauId: this.form.zauId
          }
          if (this.dialogType === 'add') {
            payload.zmParentId = this.form.zmParentId
            payload.zmLevel = this.form.zmLevel
            await addZqsxModel(payload)
            this.$message.success('新增成功')
            // 新增成功后保留当前父级状态：不关闭弹窗、不跳转，
            // 重置表单（保留父级信息），刷新右侧表格与左侧树
            const parentId = this.form.zmParentId
            const parentName = this.form._parentName
            const parentLevel = this.form.zmLevel
            this.form = this.buildForm()
            this.form.zmParentId = parentId
            this.form.zmLevel = parentLevel
            this.form._parentName = parentName
            // 刷新数据（保留 currentNode 不变）
            await this.refreshCurrent()
            this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
          } else {
            payload.zmId = this.form.zmId
            await updateZqsxModel(payload)
            this.$message.success('编辑成功')
            this.dialogVisible = false
            await this.refreshCurrent()
          }
        } catch (e) {} finally {
          this.submitLoading = false
        }
      })
    },
    async handleDelete (row) {
      try { await this.$confirm(`确定删除模块「${row.zmTitle}」？该操作会一并删除其所有子模块。`, '提示', { type: 'warning' }) } catch { return }
      try {
        await deleteZqsxModel({ zmId: row.zmId })
        this.$message.success('删除成功')
        this.fetchTree()
      } catch (e) {}
    },

    // 导出导入模板（Excel 空模板，用于填写后批量导入）
    async handleExportTemplate () {
      this.exportLoading = true
      try {
        const blob = await exportZqsxModelTemplate()
        // 构造下载链接并触发浏览器下载
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.download = '模块导入模板.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$message.success('模板导出成功')
      } catch (e) {
        // blob 错误时尝试解析后端返回的 JSON 错误信息
        if (e && e.response && e.response.data instanceof Blob) {
          const text = await e.response.data.text()
          try {
            const json = JSON.parse(text)
            this.$message.error(json.msg || '导出失败')
          } catch {
            this.$message.error('导出失败')
          }
        }
      } finally {
        this.exportLoading = false
      }
    },

    // 打开批量导入弹框
    openImportDialog () {
      this.importDialogVisible = true
    },

    // 弹框打开时：构建父级模块选项 + 重置表单
    onImportDialogOpen () {
      this.importParentId = this.currentNode ? this.currentNode.zmId : 0
      this.importParentPath = this.importParentId || []
      this.importFile = null
      this.importFileList = []
      // 构建"一级模块"为根的树形选项
      this.parentOptions = this.buildParentOptions()
    },

    // 构建父级模块级联选项：顶层固定"一级模块"，下方为树形模块
    buildParentOptions () {
      // 递归转换 treeData 为级联所需结构 { value, label, children }
      const convert = (nodes) => {
        if (!nodes || !nodes.length) return []
        return nodes.map(n => ({
          value: n.zmId,
          label: n.zmTitle,
          children: n.children && n.children.length ? convert(n.children) : undefined
        }))
      }
      return [
        { value: 0, label: '一级模块', children: convert(this.treeData) }
      ]
    },

    // 父级选择变化
    onParentChange (val) {
      this.importParentId = (val === 0 || val === undefined || val === null) ? 0 : val
    },

    // 文件选择变化（el-upload 手动上传）
    onImportFileChange (file) {
      this.importFile = file.raw
      this.importFileList = [file]
    },

    // 文件移除
    onImportFileRemove () {
      this.importFile = null
      this.importFileList = []
    },

    // 提交导入
    async submitImport () {
      if (!this.importFile) {
        this.$message.warning('请先选择 Excel 文件')
        return
      }
      this.importLoading = true
      try {
        const res = await importZqsxModelCer({ file: this.importFile, zmParentId: this.importParentId })
        this.importResult = res.data
        this.importDialogVisible = false
        this.importResultVisible = true
        // 导入成功后刷新树和列表
        this.fetchTree()
      } catch (e) {
        // 失败信息已由 request 拦截器统一提示
      } finally {
        this.importLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.model-manage {
  display: flex;
  flex-direction: column;
}
.model-manage__body {
  display: flex;
  gap: 16px;
  height: calc(100vh - 200px);
  min-height: 400px;
}

/* 左侧树 */
.model-tree {
  width: 360px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.model-tree__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}
.model-tree__title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 4px; height: 16px;
    background: linear-gradient(180deg, var(--fn-primary), var(--fn-accent));
    border-radius: 2px;
  }
}
.model-tree__filter {
  margin: 12px;
  width: calc(100% - 24px);
}
.model-tree__wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px 12px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
}
.model-tree-node {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  padding-right: 4px;
  i { color: var(--fn-primary); font-size: 15px; }
}
.model-tree-node__img {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
}
/* 内容形式徽章（小尺寸版，同表格 model-content-tag 配色） */
.model-tree-node__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  color: #fff;
  flex-shrink: 0;
  i { color: #fff; font-size: 14px; }
}
.model-tree-node__label {
  flex: 1;
  /* 限制最大宽度，超出省略号；为 tag + 删除按钮 + 子级缩进留出空间 */
  max-width: 180px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* 删除按钮（常驻文字类型） */
/* 删除按钮 tag：轻量 danger 样式，可点击 */
.model-tree-node__delete {
  cursor: pointer;
  flex-shrink: 0;
  &:hover { opacity: 0.75; }
}

/* 内容形式图标列标识（图文/证书/图片/视频/文件/链接） */
.model-content-tag {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  color: #fff;
  font-size: 11px;
  line-height: 1;
  i { font-size: 16px; color: #fff; }
}
/* 图文：科技蓝（默认） */
.model-content-tag--1 { background: linear-gradient(135deg, #409eff, #1677ff); }
/* 证书：金色 */
.model-content-tag--2 { background: linear-gradient(135deg, #faad14, #d48806); }
/* 证书类型同样走金色（zmLayoutThreeForm=2 时也用 --2） */
.model-content-tag--certificate { background: linear-gradient(135deg, #faad14, #d48806); }
/* 列表/网格形式：青色渐变（区别于内容的蓝色/金色） */
.model-content-tag--layout { background: linear-gradient(135deg, #13c2c2, #08979c); }

/* 右侧表格 */
.model-table {
  flex: 1;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
/* 当前父级信息卡片 */
.model-parent-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(26, 54, 93, 0.06) 0%, rgba(56, 161, 105, 0.04) 100%);
  border-bottom: 1px solid #f1f5f9;
  border-radius: 12px 12px 0 0;
}
.model-parent-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.model-parent-card__icon--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--fn-primary), var(--fn-accent));
  i { color: #fff; font-size: 24px; }
}
.model-parent-card__info {
  flex: 1;
  min-width: 0;
}
.model-parent-card__title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.model-parent-card__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.model-parent-card__sub {
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.model-parent-card__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* 父级内容预览弹窗 */
.parent-preview {
  min-height: 200px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}
/* 图片多张：纵向堆叠 */
.parent-preview__images {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.parent-preview__image-item {
  max-width: 100%;
  max-height: 480px;
  border-radius: 8px;
}
/* 视频多个 */
.parent-preview__videos {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}
.parent-preview__video-item {
  max-width: 100%;
  max-height: 480px;
  border-radius: 8px;
}
/* 文件多个 */
.parent-preview__files {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.parent-preview__file-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  > i:first-child { color: var(--fn-primary, #1a365d); font-size: 20px; }
}
/* 链接多个 */
.parent-preview__links {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.parent-preview__link-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  > i:first-child { color: var(--fn-primary, #1a365d); font-size: 20px; }
}
.parent-preview__file-name {
  flex: 1;
  font-size: 13px;
  color: #475569;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.parent-preview__link-url {
  flex: 1;
  font-size: 13px;
  color: #64748b;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.parent-preview__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #94a3b8;
  padding: 60px 0;
  i { font-size: 48px; }
  span { font-size: 14px; }
}

/* 新增/编辑弹窗：压缩表单项间距，避免拥挤 */
::v-deep .model-dialog .el-dialog__body {
  padding: 16px 24px;
}
::v-deep .model-dialog .el-form-item {
  margin-bottom: 16px;
}
::v-deep .model-dialog .el-form-item__content {
  line-height: 1.6;
}

/* 多类型组合容器 */
.content-groups {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
/* 单个类型行：卡片化，与其他行视觉分隔 */
.content-group {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px 16px;
  transition: border-color 0.2s;
  &:hover { border-color: #cbd5e1; }
}
/* 卡片头部：类型徽标 + 选择器 + 数量 + 移除按钮 */
.content-group__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
/* 类型徽标（彩色小标签，直观区分类型） */
.content-group__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 28px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  flex-shrink: 0;
}
.content-group__badge--1 { background: linear-gradient(135deg, #409eff, #1677ff); }
.content-group__badge--2 { background: linear-gradient(135deg, #722ed1, #b37feb); }
.content-group__badge--3 { background: linear-gradient(135deg, #faad14, #d48806); }
.content-group__badge--4 { background: linear-gradient(135deg, #52c41a, #95de64); }
.content-group__badge--5 { background: linear-gradient(135deg, #13c2c2, #08979c); }
/* 数量统计 */
.content-group__count {
  flex: 1;
  font-size: 12px;
  color: #94a3b8;
}
/* 移除按钮 */
.content-group__del {
  flex-shrink: 0;
  color: #ef4444 !important;
  &:hover { opacity: 0.75; }
}
/* 卡片主体：上传区/链接输入 */
.content-group__body {
  width: 100%;
}
/* 底部添加按钮区 */
.content-groups__add {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
  .form-tip { margin-top: 0; }
}

/* 链接动态表单 */
.link-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.link-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  .el-input { flex: 1; }
}

/* 详情弹框：URL 多行列表 */
.detail-url-line {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 13px;
  line-height: 1.8;
  word-break: break-all;
}
.detail-url-idx {
  color: #94a3b8;
  flex-shrink: 0;
}
/* 详情弹框：文本域内容块（保留换行） */
.detail-text-block {
  font-size: 13px;
  color: #475569;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 6px;
}
/* 父级预览弹框：文本域 */
.parent-preview__text {
  font-size: 13px;
  color: #475569;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

/* 表格标题栏 */
.model-table__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}
.model-table__title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}
/* 工具栏按钮组 */
.model-table__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 模块详情弹框 */
.model-detail {
  &__header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 8px 0 20px;
    border-bottom: 1px solid #f1f5f9;
    margin-bottom: 20px;
  }
  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    flex-shrink: 0;
    &--empty {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f1f5f9;
      color: #cbd5e1;
      font-size: 24px;
    }
  }
  &__badge {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    font-size: 12px;
    flex-shrink: 0;
    i { font-size: 20px; }
  }
  &__title-wrap { flex: 1; min-width: 0; }
  &__title {
    font-size: 17px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__tags {
    display: flex;
    gap: 6px;
  }
  &__info {
    margin-top: 4px;
  }
}

/* 导入结果弹窗 */
.import-result {
  &__summary {
    display: flex;
    justify-content: center;
    gap: 48px;
    padding: 16px 0 24px;
  }
  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    &--success .import-result__num { color: #67c23a; }
    &--fail .import-result__num { color: #f56c6c; }
  }
  &__num {
    font-size: 32px;
    font-weight: 700;
    color: #1e293b;
    line-height: 1;
  }
  &__label {
    font-size: 13px;
    color: #64748b;
  }
  &__table {
    margin-top: 4px;
  }
}

.form-tip {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.6;
  margin-top: 4px;
  i { margin-right: 2px; }
  a { color: var(--fn-primary); }
}

@media screen and (max-width: 992px) {
  .model-manage__body { flex-direction: column; height: auto; }
  .model-tree { width: 100%; max-height: 320px; }
}
</style>
